import React, { useState, useEffect, useCallback } from "react";
import Page from "../Page.js";
import PageContentWrapper from "../../components/PageContentWrapper.js";
import uiManager from '../../../config/ui.js';
import PAGES from '../../data/PagesConstants.js';
import { toast } from "react-toastify";
import Loading from "../../components/Loading.js";
import { postVersionUpdate, fetchChargingStations } from "../../api/csDiagnosticsApi.js";
import CsVersionUpdate from "../../models/CsVersionUpdate.js";
import FormPartial from "./FormPartial.js";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationTriangle, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { ModalVersionDisplay, StationInfoDisplay } from "./versionDisplayUtils.js";
import "./CsVersionUpdate.scss";

const Main = () => {
  const [csVersionUpdate, setCsVersionUpdate] = useState(new CsVersionUpdate());
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [chargingStations, setChargingStations] = useState([]);
  const [modalStations, setModalStations] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const refreshChargingStations = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchChargingStations();
      if (data.length > 0) {
        setChargingStations(data);
      } else {
        toast.warning('Charging stations are null or not found.');
      }
      return data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Something went wrong !');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshChargingStations();
  }, [refreshChargingStations]);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleShowModal = async () => {
    const freshData = await refreshChargingStations();
    const selectedIds = csVersionUpdate.device_ids || [];
    const stationsData = freshData.filter(s => selectedIds.includes(s.device_id));

    const parseAndroidFromDevice = (deviceStr) => {
      if (!deviceStr) return { appVersion: null, appVersionCode: null };
      const versionMatch = deviceStr.match(/AppVersion\s*:\s*([\d.]+)/);
      const codeMatch = deviceStr.match(/AppVersionCode\s*:\s*(\d+)/);
      return {
        appVersion: versionMatch?.[1] ?? null,
        appVersionCode: codeMatch?.[1] ?? null,
      };
    };

    const parseAndroidFromFilename = (filename) => {
      if (!filename) return { appVersion: null, appVersionCode: null };
      const newFmt = filename.match(/_V([\d.]+)_(\d{8})(?:_|\.apk)/i);
      if (newFmt) return { appVersion: newFmt[1], appVersionCode: newFmt[2] };
      const legacyFmt = filename.match(/_V([\d.]+)\.(\d{8})\.apk$/i);
      if (legacyFmt) return { appVersion: legacyFmt[1], appVersionCode: legacyFmt[2] };
      return { appVersion: null, appVersionCode: null };
    };

    const normalizeBinFilename = (value) => {
      if (!value) return null;
      const token = value.trim();
      const withExt = /\.bin$/i.test(token) ? token : `${token}.bin`;
      return withExt.toLowerCase();
    };

    const extractBinFilenames = (deviceStr) => {
      if (!deviceStr) return [];
      const regex = /NO\.\d+\s*:\s*(\S+)/g;
      const filenames = [];
      let match;
      while ((match = regex.exec(deviceStr)) !== null) {
        filenames.push(normalizeBinFilename(match[1]));
      }
      return filenames;
    };

    const preparedStations = stationsData.map(station => {
      let isUpToDate = false;
      let currentVersionDisplay = '--';

      if (csVersionUpdate.type === 'android') {
        const selected = parseAndroidFromFilename(csVersionUpdate.version);
        const device = parseAndroidFromDevice(station.current_android_version);

        isUpToDate =
          selected.appVersion && selected.appVersionCode &&
          device.appVersion && device.appVersionCode &&
          selected.appVersion === device.appVersion &&
          selected.appVersionCode === device.appVersionCode;
        currentVersionDisplay = station.current_android_version || '--';
      } else {
        const selectedBin = normalizeBinFilename(csVersionUpdate.version);
        const deviceBins = extractBinFilenames(station.current_bin_version);

        isUpToDate =
          selectedBin &&
          deviceBins.length > 0 &&
          deviceBins.every((f) => f === selectedBin);
        currentVersionDisplay = station.current_bin_version || '--';
      }

      return {
        ...station,
        currentVersion: currentVersionDisplay,
        isUpToDate: isUpToDate,
        selected: !isUpToDate // Default checked if needs update
      };
    });

    setModalStations(preparedStations);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const toggleStationSelection = (deviceId) => {
    setModalStations(prev => prev.map(s =>
      s.device_id === deviceId ? { ...s, selected: !s.selected } : s
    ));
  };

  const onSubmit = async (params) => {
    try {
      setIsLoading(true);
      await postVersionUpdate(params)
      .then((response) => {
        if(response.status === 200){
          toast.success(
            <>
              Version updated successfully for {modalStations.filter(s => s.selected).length} station/s.
              <br />
              Please check if updated after a while.
            </>
          );
        }
      })
      .catch(error => {
        toast.error(error?.message || 'Something went wrong!' );
      });
    } catch(error) {
      toast.error(error?.data?.message || 'Something went wrong !');
    } finally {
      handleCloseModal();
      setIsLoading(false);
    }
  }

  const onSubmitClick = async () => {
    try {
      if (new CsVersionUpdate(csVersionUpdate).toApiJson()) {
        await handleShowModal();
      }
    } catch (error) {
      setCsVersionUpdate(new CsVersionUpdate(csVersionUpdate));
    }
  }

  const onConfirmSubmitClick = () => {
    const selectedToUpdate = modalStations.filter(s => s.selected).map(s => s.device_id);

    if (selectedToUpdate.length === 0) {
      toast.warning("Please select at least one station to update.");
      return;
    }

    const payload = new CsVersionUpdate(csVersionUpdate).toApiJson();
    payload.cs_version_update.device_id = selectedToUpdate.join(',');

    onSubmit(payload);
  }

  const beautifyString = (str) => {
    return str ? str.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ") : "- -";
  }

  const isMissingVersion = (station) => {
    const raw = csVersionUpdate.type === 'android'
      ? station.current_android_version
      : station.current_bin_version;
    return !raw || raw === '--';
  };

  const renderStatusBadge = (station) => {
    if (isMissingVersion(station)) {
      return (
        <span className="cs-vup-status-badge cs-vup-status-badge--danger">
          <FontAwesomeIcon icon={faCircleXmark} />
          Missing Version
        </span>
      );
    }
    if (station.isUpToDate) {
      return (
        <span className="cs-vup-status-badge cs-vup-status-badge--success">
          <FontAwesomeIcon icon={faCheckCircle} />
          Up To Date
        </span>
      );
    }
    return (
      <span className="cs-vup-status-badge cs-vup-status-badge--warning">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        Needs Update
      </span>
    );
  };

  return <Page
    uiPage={uiManager.getUiPage(PAGES.ChargingStationVersionUpdatePage.name)}
    breadcrumbs={[{ label: "Home", url: '/dashboard' }, { label: "Charging Station Version Update" }]}
    className={'charging-station-version-update-page cs-version-update-page'}
  >
    <PageContentWrapper contentHeading={"Charging Station Version Update"}>
      {isLoading && <Loading />}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
        className="cs-vup-modal"
      >
        <Modal.Header
          style={{ backgroundImage: 'linear-gradient(103deg, #49c12b 0, #197800 99%)' }}
          className="text-white"
          closeButton
        >
          <Modal.Title>Confirm Version Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='d-flex justify-content-between align-items-center mb-4 px-2'>
            <div>
              <h5 className="mb-0 fw-bold">Update Summary</h5>
              <p className="text-muted small mb-0">Review and select stations for {beautifyString(csVersionUpdate.type)} update</p>
            </div>
            <div className="bg-voltup-theme bg-opacity-10 px-3 py-2 rounded-pill">
              <span className="fw-bold text-voltup-theme">{modalStations.filter(s => s.selected).length} / {modalStations.length}</span>
              <span className="ms-1 small text-muted">Stations Selected</span>
            </div>
          </div>

          <div className="modal-table-container custom-scrollbar mb-4">
            <table className="table cs-modal-table">
              <colgroup>
                <col className="cs-vup-modal-table__col-select" />
                <col className="cs-vup-modal-table__col-station" />
                <col className="cs-vup-modal-table__col-version" />
                <col className="cs-vup-modal-table__col-status" />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Station Information</th>
                  <th>Current Version</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalStations.map((station, index) => (
                  <tr key={station.device_id} className={station.selected ? 'row-selected' : ''}>
                    <td>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={station.selected}
                          onChange={() => toggleStationSelection(station.device_id)}
                        />
                      </div>
                    </td>
                    <td className="cs-vup-modal-table__station-cell">
                      <StationInfoDisplay
                        deviceId={station.device_id}
                        chargingZone={station.charging_zone}
                      />
                    </td>
                    <td className="cs-vup-modal-table__version-cell">
                      <ModalVersionDisplay
                        value={station.currentVersion}
                        type={csVersionUpdate.type}
                        showCopy={station.currentVersion !== '--' && station.currentVersion !== null}
                        onCopy={() => handleCopy(station.currentVersion, station.device_id)}
                        copyActive={copiedId === station.device_id}
                      />
                    </td>
                    <td className="cs-vup-modal-table__status-cell">
                      {renderStatusBadge(station)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cs-vup-target-version">
            <div className="cs-vup-version-display__item">
              <span className="cs-vup-version-display__label">Target Version</span>
              <span className="cs-vup-version-display__value">{csVersionUpdate.version}</span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCloseModal}
          >
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-voltup-theme px-4'
            disabled={modalStations.filter(s => s.selected).length === 0}
            onClick={onConfirmSubmitClick}
          >
            Confirm & Update
          </button>
        </Modal.Footer>
      </Modal>
      <div className="cs-vup-page-content">
        <form className="row g-4" autoComplete="off">
          <FormPartial
            csVersionUpdate={csVersionUpdate}
            setCsVersionUpdate={setCsVersionUpdate}
            chargingStations={chargingStations}
            refreshChargingStations={refreshChargingStations}
            onSubmitClick={onSubmitClick}
          />
        </form>
      </div>
    </PageContentWrapper>
  </Page>
};

export default Main;
